import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Checkbox, Spin } from 'antd';
import { useStores } from 'Hooks/useStore';
import { Status } from 'app-types';
import styles from './AssignmentActionFlowModal.module.scss';
import { resolveAssignmentUserIds } from './resolveAssignmentUserIds';

const SCOPE_LABELS: Record<string, string> = {
	roles: 'Roles',
	groups: 'Groups',
	group_labels: 'Organization',
	users: 'Users'
};

interface Props {
	assignmentId: number;
	scopes: string[];
	tenantId?: number;
	selectedUserIds: string[];
	onToggle: (userId: string) => void;
}

const AssignmentUsersPicker: React.FC<Props> = observer(
	({ assignmentId, scopes, tenantId, selectedUserIds, onToggle }) => {
		const { learningManagerStore, assignUsersStore } = useStores();
		const { assignmentUsers } = assignUsersStore;
		const [isResolving, setIsResolving] = useState(false);
		const scopesKey = scopes.join(',');

		useEffect(() => {
			if (!assignmentId) return;
			let cancelled = false;
			(async () => {
				setIsResolving(true);
				try {
					const userIds = await resolveAssignmentUserIds(
						learningManagerStore,
						assignUsersStore,
						assignmentId,
						scopesKey.split(','),
						tenantId
					);
					if (!cancelled) {
						await assignUsersStore.fetchUsersByIds(userIds);
					}
				} catch {
					// failures are reflected via the status flags below
				} finally {
					if (!cancelled) setIsResolving(false);
				}
			})();
			return () => {
				cancelled = true;
			};
		}, [
			assignmentId,
			scopesKey,
			tenantId,
			learningManagerStore,
			assignUsersStore
		]);

		// The scope endpoints return ids without names, so the badges name the
		// scope type rather than the individual entities.
		const badges = scopes
			.filter((scope) => scope !== 'users')
			.map((scope) => SCOPE_LABELS[scope] ?? scope);

		const isLoading =
			isResolving ||
			assignUsersStore.status.fetchUsersByIds === Status.Pending;

		return (
			<div id="lms-ca-assignment-users-picker">
				{badges.length > 0 && (
					<div className={styles.scopeBadges}>
						{badges.map((label, index) => (
							<span
								key={`${label}-${index}`}
								className={styles.scopeBadge}
							>
								{label}
							</span>
						))}
					</div>
				)}

				<div className={styles.usersHeader}>
					Users ({assignmentUsers.length})
				</div>

				{isLoading ? (
					<div className={styles.pickerLoading}>
						<Spin />
					</div>
				) : assignmentUsers.length === 0 ? (
					<div className={styles.pickerEmpty}>
						No users associated with this assignment.
					</div>
				) : (
					<div className={styles.userList}>
						{assignmentUsers.map((user) => (
							<div
								key={user.id}
								className={styles.userRow}
								onClick={() => onToggle(user.id)}
							>
								<Checkbox
									checked={selectedUserIds.includes(user.id)}
									onChange={() => undefined}
									style={{ pointerEvents: 'none' }}
								/>
								<div className={styles.userInfo}>
									<div className={styles.userName}>
										{user.firstName} {user.lastName}
									</div>
									<div className={styles.userEmail}>
										{user.email}
									</div>
								</div>
							</div>
						))}
					</div>
				)}
			</div>
		);
	}
);

export default AssignmentUsersPicker;
